import Phaser from 'phaser';
import type { PetDef, PetInstance } from '@/domain/types/pet';
import { VARIANT_MULT, VARIANT_COIN_BONUS } from '@/domain/types/pet';
import { PET, PET_RETURN_SPEED_MULT, PET_SLOTS } from '@/config/balance.config';
import { activeEffectMult } from '@/config/potions.config';
import { saveManager } from '@/save';
import { PetAnimator } from '@/game/fx/PetAnimator';
import { showDamagePopup } from '@/game/fx/DamagePopup';
import { Haptics } from '@/game/fx/Haptics';
import {
  variantScaleMult,
  applyVariantFx,
  destroyVariantFx,
  variantHasGoldSparkles,
  spawnAmbientSparkleSprite,
  SPARKLE_SPRITE_INTERVAL_MS,
  spawnSparkleBurst,
  spawnRainbowTrailAfterimage,
  RAINBOW_TRAIL_INTERVAL_MS,
  RAINBOW_TRAIL_SPEED_THRESHOLD,
  type VariantFxRefs,
} from '@/game/fx/variantFx';
import type { Player } from './Player';
import type { PetTarget } from './PetTarget';

/**
 * Stan automatu FSM peta. Patrz GAME_DESIGN §5.2.
 *
 *   IDLE_FOLLOW       — pet leci w formacji za graczem (slot offset)
 *   MOVING_TO_TARGET  — namierzony destructible, zbliża się do niego
 *   ATTACKING         — w zasięgu attackRange, tickuje damage
 *   RETURNING         — wraca do slot'u po destrukcji / utracie targetu
 *
 * Logika FSM modyfikuje LOGICZNĄ pozycję (logicalX/Y). PetAnimator nakłada
 * efekty wizualne (bob, breath, tilt, squish) na sprite — sprite.x/y są
 * obliczane jako logicalPos + animator offsets.
 */
export type PetState = 'IDLE_FOLLOW' | 'MOVING_TO_TARGET' | 'ATTACKING' | 'RETURNING';
export type PetPositionConstraint = (x: number, y: number) => { x: number; y: number };

const SPRITE_DISPLAY_SIZE = 48;       // px — docelowy rozmiar peta na ekranie

// Sesja 29B: variant FX stałe (GOLD_TINT, RAINBOW_CYCLE_MS, AURA_RADIUS itd.)
// + funkcje wyciągnięte do `src/game/fx/variantFx.ts` (DRY z HatchDirector).
// variantScaleMult (huge/titanic skala) importowane stąd dla konstruktora.

export class Pet {
  readonly sprite: Phaser.GameObjects.Image;
  readonly animator: PetAnimator;
  state: PetState = 'IDLE_FOLLOW';

  readonly uid: string;
  private target: PetTarget | null = null;
  private lastAttackAt = 0;
  readonly power: number;
  /** Bonus do złota wnoszony przez tego peta (VARIANT_COIN_BONUS). 0 dla normal. */
  readonly coinBonus: number;

  // Logiczna pozycja peta (gdzie naprawdę "jest"). Sprite.x/y = to + animator offset.
  private logicalX: number;
  private logicalY: number;

  // Do liczenia velocity dla animatora (smoothed)
  private prevLogicalX: number;
  private prevLogicalY: number;

  // ── Variant FX (sesja 23d → sesja 29B refactor) ────────────────────────
  // Funkcje aplikujące w `src/game/fx/variantFx.ts` — wspólne z HatchDirector.
  // Pet implementuje `VariantFxOwner` (mutable rainbowHue + goldShimmerProgress
  // dla tween onUpdate). `installVariantFx()` przekazuje `this` jako owner.
  // refs (aura + tweens) trzymane do destroy().
  rainbowHue = 0;
  goldShimmerProgress = 0;
  private variantFxRefs: VariantFxRefs | null = null;
  private nextAmbientSparkleAt = 0;
  private nextRainbowTrailAt = 0;

  constructor(
    private readonly scene: Phaser.Scene,
    readonly def: PetDef,
    readonly instance: PetInstance,
    public slotIndex: number,
    public slotCount: number,
    private readonly player: Player,
  ) {
    this.uid = instance.uid;
    this.power = def.basePower * VARIANT_MULT[instance.variant];
    this.coinBonus = VARIANT_COIN_BONUS[instance.variant];

    // Pozycja startowa = pozycja gracza (animator zaraz "wciągnie" peta do slotu)
    this.logicalX = player.x;
    this.logicalY = player.y;
    this.prevLogicalX = this.logicalX;
    this.prevLogicalY = this.logicalY;

    const tex = scene.textures.exists(def.art.textureKey)
      ? def.art.textureKey
      : 'placeholder-square';

    this.sprite = scene.add.image(this.logicalX, this.logicalY, tex);
    // Skalujemy sprite tak, żeby najdłuższy bok = SPRITE_DISPLAY_SIZE.
    // HUGE/TITANIC pety dostają ekstra mnożnik (×1.45 / ×1.9) — są wizualnie
    // wyraźnie większe, bo gracz zdobywa je bardzo rzadko i powinien od razu
    // widzieć że to coś specjalnego.
    const sizeMult = (def.art.scale ?? 1) * variantScaleMult(instance.variant);
    const baseScale = SPRITE_DISPLAY_SIZE / Math.max(this.sprite.width, this.sprite.height) * sizeMult;
    this.sprite.setScale(baseScale);
    this.sprite.setDepth(5);

    this.animator = new PetAnimator(scene, this.sprite, baseScale, this.logicalX, this.logicalY);

    // ── Variant FX setup (sesja 23d → sesja 29B: variantFx.ts wspólny z HatchDirector) ─
    // Aura position = sprite position (czyli `(logicalX, logicalY)` przed animator offset).
    this.variantFxRefs = applyVariantFx(
      scene,
      this.sprite,
      instance.variant,
      this,                                  // Pet implementuje VariantFxOwner (rainbowHue + goldShimmerProgress)
      { x: this.logicalX, y: this.logicalY },
      undefined,                             // auraRadius default
      { sparkleEmitter: true },              // ambient iskry gold/rainbow — tylko świat (max 6 petów)
    );
    this.nextAmbientSparkleAt = scene.time.now + Phaser.Math.Between(0, SPARKLE_SPRITE_INTERVAL_MS);
    this.nextRainbowTrailAt = scene.time.now + Phaser.Math.Between(0, RAINBOW_TRAIL_INTERVAL_MS);
  }

  /**
   * Niszczy wszystkie zasoby Phaser tego peta. Wołane przez
   * `WorldScene.respawnEquippedPets()` przed re-spawn'em.
   *
   * Sesja 23: centralizujemy cleanup w jednej metodzie.
   * Sesja 29B: variant FX (tweens + aura) sprzątane przez `destroyVariantFx`
   * z `variantFx.ts` zamiast inline. Spójność z HatchDirector cleanup.
   */
  destroy(): void {
    if (this.variantFxRefs) {
      destroyVariantFx(this.variantFxRefs);
      this.variantFxRefs = null;
    }
    this.animator.destroy();
    this.sprite.destroy();
  }

  private slotPosition(): { x: number; y: number } {
    const angle = (this.slotIndex / Math.max(1, this.slotCount)) * Math.PI * 2;
    return {
      x: this.player.x + Math.cos(angle) * PET_SLOTS.formationRadius,
      y: this.player.y + Math.sin(angle) * PET_SLOTS.formationRadius,
    };
  }

  assignTarget(dest: PetTarget): boolean {
    if (this.state !== 'IDLE_FOLLOW') return false;
    if (!dest.hasFreeSlot()) return false;
    this.target = dest;
    dest.attackers.add(this.uid);
    this.state = 'MOVING_TO_TARGET';
    return true;
  }

  isIdle(): boolean {
    return this.state === 'IDLE_FOLLOW';
  }

  /**
   * Odepchnięcie peta od punktu (np. fala uderzeniowa bossa): przesuwa logiczną
   * pozycję w bok od źródła i wytrąca z ataku (RETURNING → ponowne podejście).
   * Czysto kinematyczne — nie zadaje obrażeń, daje „dynamikę" walki arenowej.
   */
  knockback(fromX: number, fromY: number, force: number): void {
    const dx = this.logicalX - fromX;
    const dy = this.logicalY - fromY;
    const len = Math.hypot(dx, dy) || 1;
    this.logicalX += (dx / len) * force;
    this.logicalY += (dy / len) * force;
    this.releaseTarget();
    this.state = 'RETURNING';
  }

  /**
   * Przyciągnięcie peta do punktu (np. wir bossa) — odwrotność `knockback`.
   * Przesuwa logiczną pozycję ku źródłu i wytrąca z ataku. Czysto kinematyczne.
   */
  pull(towardX: number, towardY: number, force: number): void {
    const dx = towardX - this.logicalX;
    const dy = towardY - this.logicalY;
    const len = Math.hypot(dx, dy) || 1;
    this.logicalX += (dx / len) * force;
    this.logicalY += (dy / len) * force;
    this.releaseTarget();
    this.state = 'RETURNING';
  }

  update(dt: number, now: number, constrainPosition?: PetPositionConstraint): void {
    // 1) FSM tick — modyfikuje logicalX/Y
    switch (this.state) {
      case 'IDLE_FOLLOW':       this.tickIdleFollow(); break;
      case 'MOVING_TO_TARGET':  this.tickMovingToTarget(dt); break;
      case 'ATTACKING':         this.tickAttacking(now); break;
      case 'RETURNING':         this.tickReturning(dt); break;
    }
    if (constrainPosition) {
      const corrected = constrainPosition(this.logicalX, this.logicalY);
      this.logicalX = corrected.x;
      this.logicalY = corrected.y;
    }

    // 2) Velocity dla animatora (px/s)
    const vx = dt > 0 ? (this.logicalX - this.prevLogicalX) / dt : 0;
    const vy = dt > 0 ? (this.logicalY - this.prevLogicalY) / dt : 0;
    this.prevLogicalX = this.logicalX;
    this.prevLogicalY = this.logicalY;

    // 3) Animator — zwraca offset X/Y (bob + lunge)
    const { offsetX, offsetY } = this.animator.update(now, dt, vx, vy, this.logicalX, this.logicalY);

    // 4) Finalna pozycja sprite'a = logiczna + offsety wizualne
    this.sprite.x = this.logicalX + offsetX;
    this.sprite.y = this.logicalY + offsetY;

    // 5) Aura (jeśli istnieje) śledzi sprite — żeby przesuwała się razem z bob'em.
    //    Sesja 29B: aura ref w `variantFxRefs.aura` zamiast `this.aura`.
    const aura = this.variantFxRefs?.aura;
    if (aura) {
      aura.x = this.sprite.x;
      aura.y = this.sprite.y;
    }

    // ParticleEmitter pozycjonujemy ręcznie co klatkę, tak jak aurę.
    for (const emitter of this.variantFxRefs?.emitters ?? []) {
      emitter.setPosition(this.sprite.x, this.sprite.y);
    }

    if (variantHasGoldSparkles(this.instance.variant) && now >= this.nextAmbientSparkleAt) {
      spawnAmbientSparkleSprite(this.scene, this.sprite.x, this.sprite.y, this.instance.variant);
      this.nextAmbientSparkleAt = now + Phaser.Math.Between(80, 150);
    }

    if (this.instance.variant === 'rainbow' && now >= this.nextRainbowTrailAt) {
      const speed = Math.hypot(vx, vy);
      if (speed >= RAINBOW_TRAIL_SPEED_THRESHOLD) {
        spawnRainbowTrailAfterimage(this.scene, this.sprite, this.rainbowHue, vx, vy);
        this.nextRainbowTrailAt = now + Phaser.Math.Between(
          RAINBOW_TRAIL_INTERVAL_MS - 18,
          RAINBOW_TRAIL_INTERVAL_MS + 26,
        );
      } else {
        this.nextRainbowTrailAt = now + RAINBOW_TRAIL_INTERVAL_MS;
      }
    }
  }

  private tickIdleFollow(): void {
    const slot = this.slotPosition();
    this.logicalX += (slot.x - this.logicalX) * PET.formationFollowLerp;
    this.logicalY += (slot.y - this.logicalY) * PET.formationFollowLerp;
  }

  private tickMovingToTarget(dt: number): void {
    if (!this.target || !this.target.isTargetable()) {
      this.releaseTarget();
      this.state = 'RETURNING';
      return;
    }
    const dx = this.target.x - this.logicalX;
    const dy = this.target.y - this.logicalY;
    const dist = Math.hypot(dx, dy);
    if (dist < PET.attackRange) {
      this.state = 'ATTACKING';
      this.lastAttackAt = 0;
      return;
    }
    const speed = PET.baseSpeed;
    this.logicalX += (dx / dist) * speed * dt;
    this.logicalY += (dy / dist) * speed * dt;
  }

  private tickAttacking(now: number): void {
    if (!this.target || !this.target.isTargetable()) {
      this.releaseTarget();
      this.state = 'RETURNING';
      return;
    }
    if (now - this.lastAttackAt < PET.attackIntervalMs) return;
    this.lastAttackAt = now;

    // Aktywny buff siły z mikstury (1 gdy brak/wygasł). Live-read — kolejne
    // uderzenie od razu uwzględni świeżo użytą miksturę. Klientowy mnożnik
    // obrażeń (spójne z modelem zaufania do walki, jak buff złota).
    const strengthMult = activeEffectMult(
      saveManager.getCurrent().activeEffects,
      'strength',
      now,
    );
    const damage = Math.round(this.power * strengthMult);

    // Kierunek do celu (znormalizowany) — dla lunge'a animatora
    const dx = this.target.x - this.logicalX;
    const dy = this.target.y - this.logicalY;
    const dist = Math.hypot(dx, dy);
    const dirX = dist > 0 ? dx / dist : 0;
    const dirY = dist > 0 ? dy / dist : 0;

    const died = this.target.takeDamage(damage);

    // FX: squish + lunge w stronę celu + damage popup + haptics
    this.animator.triggerAttackHit(dirX, dirY);
    // Gold/rainbow tryska iskrami w punkcie uderzenia.
    // No-op dla normal/huge/titanic (gating po fladze VARIANT_VISUALS.sparkles).
    spawnSparkleBurst(this.scene, this.target.x, this.target.y, this.instance.variant);
    if (this.target.showDamageNumbers !== false) {
      showDamagePopup(this.scene, this.target.x, this.target.y - 8, damage);
    }
    Haptics.petAttack();

    if (died) {
      this.releaseTarget();
      this.state = 'RETURNING';
    }
  }

  private tickReturning(dt: number): void {
    const slot = this.slotPosition();
    const dx = slot.x - this.logicalX;
    const dy = slot.y - this.logicalY;
    const dist = Math.hypot(dx, dy);
    if (dist < PET.slotReachThreshold) {
      this.state = 'IDLE_FOLLOW';
      return;
    }
    const speed = PET.baseSpeed * PET_RETURN_SPEED_MULT;
    this.logicalX += (dx / dist) * speed * dt;
    this.logicalY += (dy / dist) * speed * dt;
  }

  private releaseTarget(): void {
    if (this.target) {
      this.target.attackers.delete(this.uid);
      this.target = null;
    }
  }
}

// `hsvToHex` przeniesione do `src/game/fx/variantFx.ts` (sesja 29B).
